import type { TrackingCookieConfig } from './customerSourceAnalytics/customerSourceAnalytics.constants';
import { destroyCookie, parseCookies, setCookie } from 'nookies';
import { useEffect } from 'react';
import { PENDING_EMAIL_VERIFIED_EVENT_KEY } from '@/components/entities/analytics/gtm/core/emailVerifiedEvent.constants';
import { writeCustomerSourceHeadersToStorage } from '@/libs/userSource/customerSourceStorage';
import { parseUserSourceHeaders } from '@/libs/userSource/parseUserSourceHeaders';
import { parseVerifyUserSourceQuery } from '@/libs/userSource/verifyUserSourceQuery';
import { clearPendingRegistration } from '@/modals/modules/auth/RegisterModal/components/registrationSession.utils';
import { setFirstVisitRegistration } from '@/modals/modules/auth/RegisterModal/visitRegistration.utils';
import { authApi } from '@/queries/auth/auth.api';
import {
  TRACKING_COOKIE_CONFIGS,
} from './customerSourceAnalytics/customerSourceAnalytics.constants';
import {
  resolveSearchParamValue,
  resolveTrackingCookieSyncAction,
} from './customerSourceAnalytics/customerSourceAnalytics.utils';

function syncTrackingCookie(
  cookies: Record<string, string>,
  searchParams: URLSearchParams,
  config: TrackingCookieConfig,
): void {
  const urlValue = resolveSearchParamValue(searchParams, config.urlParamNames);
  const action = resolveTrackingCookieSyncAction(cookies[config.cookieName], urlValue);

  if (action.type === 'set') {
    setCookie(null, config.cookieName, action.value, {
      path: '/',
      maxAge: config.maxAgeSeconds,
      sameSite: 'lax',
    });
    return;
  }

  if (action.type === 'remove') {
    destroyCookie(null, config.cookieName, { path: '/' });
  }
}

function syncFbcAndClickIdCookies(cookies: Record<string, string>, sourceHeaders: Record<string, string>): void {
  if (!cookies._fbc && sourceHeaders['x-fbc']) {
    const timestamp = Date.now();
    const fbcValue = `fb.1.${timestamp}.${sourceHeaders['x-fbc']}`;
    setCookie(null, '_fbc', fbcValue);
  }

  if (sourceHeaders['x-click-id'] && cookies.click_id !== sourceHeaders['x-click-id']) {
    setCookie(null, 'click_id', sourceHeaders['x-click-id']);
  }
}

function syncAttributionData(searchParams: URLSearchParams, href: string): void {
  const sourceHeaders = parseUserSourceHeaders(href);
  writeCustomerSourceHeadersToStorage(sourceHeaders);

  const cookies = parseCookies();

  TRACKING_COOKIE_CONFIGS.forEach(config => syncTrackingCookie(cookies, searchParams, config));
  syncFbcAndClickIdCookies(cookies, sourceHeaders);
}

function verifyEmailSourceIfNeeded(searchParams: URLSearchParams): void {
  const currentVerifyToken = searchParams.get('token');
  const currentEventId = currentVerifyToken ? `verify:${currentVerifyToken}` : null;
  const { shouldVerifyUserSource, token, deviceId } = parseVerifyUserSourceQuery(searchParams);

  if (!shouldVerifyUserSource || !token) {
    return;
  }

  authApi.verifyEmail({
    token,
    ...(deviceId ? { deviceId } : {}),
  })
    .then((verifyEmailResponse) => {
      clearPendingRegistration();
      const redirectUrl = verifyEmailResponse?.redirectUrl;
      const willRedirect = Boolean(redirectUrl && redirectUrl !== window.location.href);
      const eventId = currentEventId ?? `verify:${token}`;

      // Always mark pending first; if redirect happens immediately, we'll replay on next load.
      window.sessionStorage.setItem(PENDING_EMAIL_VERIFIED_EVENT_KEY, eventId);

      if (willRedirect && redirectUrl) {
        window.location.replace(redirectUrl);
      }
    })
    .catch(() => {
      // Ignore verification errors to avoid blocking analytics setup.
    });
}

export default function CustomerSourceAnalytics() {
  useEffect(() => {
    const { searchParams, href } = new URL(window.location.href);
    syncAttributionData(searchParams, href);
  }, []);

  useEffect(() => {
    const { searchParams } = new URL(window.location.href);
    // FYI: We don't need to open login modal again, after verified email
    // When we made registration, we set first visit registration to true to open login modal, if user visited another tab and return to the site
    setFirstVisitRegistration(false);

    verifyEmailSourceIfNeeded(searchParams);
  }, []);

  return null;
}
